import React from "react";
import { Button, Select, Input } from "antd";
const { Option } = Select;

const ConditionGroup = ({ group, onChange, fieldlist, isRoot = false }) => {
  const handleUpdateChild = (idx, updated) => {
    const updatedConditions = [...group.conditions];
    updatedConditions[idx] = updated;
    onChange({ ...group, conditions: updatedConditions });
  };

  const handleAddCondition = () => {
    const newCond = {
      type: "condition",
      field: "",
      operator: "==",
      value: "",
      // NEW: default valueType keeps old behavior as string
      valueType: "string",
    };
    const updated = {
      ...group,
      conditions: [...(group.conditions || []), newCond],
    };
    onChange(updated);
  };

  const handleAddGroup = () => {
    const newGroup = { type: "OR", conditions: [] };
    const updated = {
      ...group,
      conditions: [...(group.conditions || []), newGroup],
    };
    onChange(updated);
  };

  const handleRemove = (idx) => {
    const updated = group.conditions.filter((_, i) => i !== idx);
    onChange({ ...group, conditions: updated });
  };

  const childrenCount = (group.conditions && group.conditions.length) || 0;
  const isSingleChild = childrenCount <= 1;

  return (
    <div className="border p-2 rounded mb-2" style={{ background: "#f8f9fa" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <b>{isRoot ? "Group" : "Sub-Group"}</b>
        <Select
          value={group.type}
          style={{ width: 100 }}
          disabled={isSingleChild}
          onChange={(val) => onChange({ ...group, type: val })}
        >
          <Option value="AND">AND</Option>
          <Option value="OR">OR</Option>
        </Select>
      </div>

      {/* Body */}
      <div style={{ marginLeft: 15 }}>
        {group.conditions.map((cond, idx) => {
          if (cond.type !== "condition") {
            // Nested group
            return (
              <div key={idx} style={{ marginBottom: 8 }}>
                <ConditionGroup
                  group={cond}
                  fieldlist={fieldlist}
                  onChange={(updated) => handleUpdateChild(idx, updated)}
                />
                <Button
                  danger
                  size="small"
                  onClick={() => handleRemove(idx)}
                  style={{ marginTop: 5 }}
                >
                  Remove Group
                </Button>
              </div>
            );
          }

          // --- Condition row ---
          // Backwards compatibility: infer type if valueType is missing
          const inferredType =
            cond.valueType ||
            (typeof cond.value === "boolean"
              ? "boolean"
              : typeof cond.value === "number"
                ? "number"
                : "string");

          const handleTypeChange = (newType) => {
            let newValue = cond.value;

            if (newType === "boolean") {
              if (typeof newValue === "boolean") {
                // keep as is
              } else if (typeof newValue === "number") {
                newValue = Boolean(newValue);
              } else if (typeof newValue === "string") {
                const lower = newValue.toLowerCase();
                if (lower === "true") newValue = true;
                else if (lower === "false") newValue = false;
                else newValue = false;
              } else {
                newValue = false;
              }
            } else if (newType === "number") {
              const num = Number(newValue);
              newValue = Number.isNaN(num) ? 0 : num;
            } else {
              // string
              newValue = newValue != null ? String(newValue) : "";
            }

            handleUpdateChild(idx, {
              ...cond,
              valueType: newType,
              value: newValue,
            });
          };

          const handleValueChange = (val) => {
            if (inferredType === "number") {
              const num = Number(val);
              handleUpdateChild(idx, {
                ...cond,
                value: Number.isNaN(num) ? undefined : num,
                valueType: inferredType,
              });
            } else if (inferredType === "boolean") {
              handleUpdateChild(idx, {
                ...cond,
                value: val === "true",
                valueType: inferredType,
              });
            } else {
              handleUpdateChild(idx, {
                ...cond,
                value: val,
                valueType: inferredType,
              });
            }
          };

          return (
            <div key={idx} style={{ marginBottom: 8 }}>
              <div className="d-flex align-items-center gap-2">
                {/* Field */}
                <Select
                  placeholder="Field"
                  style={{ width: 150 }}
                  value={cond.field}
                  onChange={(val) =>
                    handleUpdateChild(idx, { ...cond, field: val })
                  }
                >
                  {fieldlist &&
                    fieldlist.map((f) => (
                      <Option key={f.BucketField} value={f.BucketField}>
                        {f.fieldname}
                      </Option>
                    ))}
                </Select>

                {/* Operator */}
                <Select
                  placeholder="Op"
                  style={{ width: 120 }}
                  value={cond.operator}
                  onChange={(val) =>
                    handleUpdateChild(idx, { ...cond, operator: val })
                  }
                >
                  {[
                    "==",
                    "===",
                    "!=",
                    "!==",
                    ">",
                    "<",
                    ">=",
                    "<=",
                    "includes",
                    "startsWith",
                    "endsWith",
                  ].map((op) => (
                    <Option key={op} value={op}>
                      {op}
                    </Option>
                  ))}
                </Select>

                {/* NEW: Value type */}
                <Select
                  style={{ width: 110 }}
                  value={inferredType}
                  onChange={handleTypeChange}
                >
                  <Option value="string">Text</Option>
                  <Option value="number">Number</Option>
                  <Option value="boolean">Boolean</Option>
                </Select>

                {/* Value input by type */}
                {inferredType === "boolean" ? (
                  <Select
                    style={{ width: 120 }}
                    value={cond.value === true ? "true" : "false"}
                    onChange={handleValueChange}
                  >
                    <Option value="true">true</Option>
                    <Option value="false">false</Option>
                  </Select>
                ) : (
                  <Input
                    placeholder="Value"
                    style={{ width: 150 }}
                    type={inferredType === "number" ? "number" : "text"}
                    value={cond.value !== null && cond.value !== undefined
                      ? cond.value
                      : ""
                    }
                    onChange={(e) => handleValueChange(e.target.value)}
                  />
                )}

                <Button danger onClick={() => handleRemove(idx)}>
                  -
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Buttons */}
      <div className="mt-2 d-flex gap-2">
        <Button size="small" type="primary" onClick={handleAddCondition}>
          + Condition
        </Button>
        <Button size="small" onClick={handleAddGroup}>
          + Sub-group
        </Button>
      </div>
    </div>
  );
};

export const ConditionBuilder = ({ group, onChange, fieldlist }) => {
  const handleAddRootGroup = () => {
    const initialGroup = {
      type: "OR",
      conditions: [],
    };
    // parent expects array
    onChange([initialGroup]);
  };

  const handleRemoveRootGroup = () => {
    onChange([]); // reset to empty array
  };

  // Defensive: if it's not an array or empty
  if (!Array.isArray(group) || group.length === 0) {
    return (
      <div className="text-center border rounded p-3">
        <Button type="primary" onClick={handleAddRootGroup}>
          + Add Condition
        </Button>
      </div>
    );
  }

  return (
    <div className="relative border rounded p-3">
      {/* Clear All */}
      <div className="absolute top-2 right-2">
        <Button type="text" danger onClick={handleRemoveRootGroup}>
          Remove All
        </Button>
      </div>

      {group.map((g, idx) => (
        <ConditionGroup
          key={idx}
          group={g}
          fieldlist={fieldlist}
          onChange={(updated) => {
            const updatedGroups = [...group];
            updatedGroups[idx] = updated;
            onChange(updatedGroups);
          }}
          isRoot
        />
      ))}
    </div>
  );
};
